import { Checkbox, TableCell, TableRow, Tooltip } from '@mui/material';
import TeamMembershipRoleSelect, {
  membershipRoleLabel,
} from 'components/@home/drawers/TeamMembershipRoleSelect';
import isEmpty from 'lodash/isEmpty';
import truncate from 'lodash/truncate';
import { bool, func, object, string } from 'prop-types';
import React, { memo, useCallback } from 'react';
import employeeShape from 'shapes/employee';
import { I18n } from 'utils/i18n';

const inactiveSx = { color: 'secondary.main' };

const getName = e =>
  [e.firstName, e.surName].filter(v => v).join(' ') + (isEmpty(e.code) ? '' : ` - ${e.code}`);

const EmployeeRow = ({
  employee,
  defaultRole = null,
  onSelect,
  onUpdateMember,
  member,
  ruleManaged = false,
}) => {
  const select = useCallback(() => {
    if (ruleManaged) return;
    onSelect(employee._id);
  }, [onSelect, employee._id, ruleManaged]);

  const handleRoleChange = useCallback(
    ({ role, isVisible }) => {
      onUpdateMember(employee, { role, isVisible });
    },
    [employee, onUpdateMember],
  );

  const isInactive = !employee.isAcceptTerms;
  const cellSx = isInactive ? inactiveSx : undefined;

  return (
    <TableRow
      hover
      sx={theme => ({
        border: 0,
        cursor: 'pointer',
        '&:nth-of-type(odd)': {
          backgroundColor: theme.palette.secondary.ultraUltraLight,
        },
      })}
      onClick={select}
      selected={!!member}
    >
      <TableCell padding="checkbox" sx={{ position: 'relative', p: 0, pl: '4px' }}>
        <Checkbox color="primary" checked={!!member} disabled={ruleManaged} />
      </TableCell>
      <TableCell sx={cellSx} padding="normal">
        <Tooltip title={getName(employee)} placement="top">
          <span>{truncate(getName(employee), { length: 18 })}</span>
        </Tooltip>
      </TableCell>
      <TableCell sx={cellSx}>
        <Tooltip title={employee.department} placement="top">
          <span>{truncate(employee.department, { length: 18 })}</span>
        </Tooltip>
      </TableCell>
      <TableCell sx={cellSx}>
        <Tooltip title={employee.jobTitle} placement="top">
          <span>{truncate(employee.jobTitle, { length: 18 })}</span>
        </Tooltip>
      </TableCell>
      <TableCell sx={cellSx}>{employee.location}</TableCell>
      <TableCell sx={isInactive ? inactiveSx : undefined}>
        {!!member &&
          (ruleManaged ? (
            <Tooltip title={I18n.t('EmployeeDrawer.RuleManagedTeam')}>
              <span>
                {membershipRoleLabel({
                  role: member.role || defaultRole,
                  isVisible: member.isVisible,
                })}
              </span>
            </Tooltip>
          ) : (
            <TeamMembershipRoleSelect
              role={member.role || defaultRole}
              isVisible={!!member.isVisible}
              onChange={handleRoleChange}
            />
          ))}
      </TableCell>
    </TableRow>
  );
};

EmployeeRow.propTypes = {
  employee: employeeShape.isRequired,
  member: object.isRequired,
  defaultRole: string,
  onSelect: func.isRequired,
  onUpdateMember: func.isRequired,
  ruleManaged: bool,
};

export default memo(EmployeeRow);
